import { useState, useEffect, useCallback } from 'react';
import { Notification } from '../types/models';
import { listNotifications, markNotificationsRead } from '../services/notifications';
import { useAuth } from '../contexts/AuthContext';

interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (ids?: string[]) => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const data = await listNotifications();
      setNotifications(data.notifications);
      setUnreadCount(data.unreadCount);
    } catch {
      // silently fail — notifications are non-critical
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
    // Poll every 60s so the badge stays current without a page reload
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const markRead = useCallback(async (ids?: string[]) => {
    await markNotificationsRead(ids);
    setNotifications(prev =>
      prev.map(n =>
        !ids || ids.includes(n.notificationId) ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => {
      if (!ids) return 0;
      const markedCount = notifications.filter(
        n => !n.read && ids.includes(n.notificationId)
      ).length;
      return Math.max(0, prev - markedCount);
    });
  }, [notifications]);

  return { notifications, unreadCount, loading, refresh, markRead };
}
